import { useState, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";

export function CookieBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      const consent = localStorage.getItem("bwj_cookie_consent");
      if (!consent) {
        setShow(true);
      }
    } catch {
      // Ignore in restricted environments
    }
  }, []);

  const handleChoice = (choice: "all" | "essential") => {
    try {
      localStorage.setItem("bwj_cookie_consent", choice);
    } catch {
      // Ignore storage errors in restricted environments
    }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      role="region"
      aria-label="Cookie consent banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-md z-50 p-5 rounded-2xl bg-card/95 backdrop-blur-md border border-border shadow-2xl text-foreground animate-in fade-in slide-in-from-bottom-4 duration-300"
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-xl bg-primary/10 text-primary shrink-0 mt-0.5">
          <ShieldCheck size={20} />
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-semibold mb-1">Privacy &amp; Cookie Choices</h4>
          <p className="text-xs text-muted-foreground leading-relaxed mb-3">
            We use cookies to maintain security, analyze reader traffic, and support free editorial
            content via Google AdSense. Review our{" "}
            <Link to="/privacy-policy" className="text-primary hover:underline font-medium">
              Privacy Policy
            </Link>{" "}
            for complete transparency.
          </p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => handleChoice("all")}
              className="text-xs h-8 px-3 font-medium cursor-pointer"
            >
              Accept All
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleChoice("essential")}
              className="text-xs h-8 px-3 font-medium cursor-pointer"
            >
              Essential Only
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => handleChoice("essential")}
          aria-label="Dismiss cookie notice"
          className="text-muted-foreground hover:text-foreground transition-colors p-1"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
